import type { BulkActionProgress } from "src/generated/hooks/form-service/requests";
import {
  getBulkActionNoun,
  partialFailureCopy,
  processingCopy,
  successCopy,
  type BulkActionType,
} from "../activity/labels";

export const BULK_ACTION_PROGRESS_POLL_MS = 1500;
export const BULK_ACTION_PROGRESS_TOAST_COMPLETE_MS = 8000;

export type BulkActionProgressSnapshot = {
  queued: number;
  processed: number;
  doneCount: number;
  errorCount: number;
  isComplete: boolean;
  progressPercent: number;
};

export type BulkActionProgressPoll = BulkActionProgress;

export type BulkActionProgressToastCopy = {
  title: string;
  description: string;
};

export function computeBulkActionProgress(
  bulkAction: BulkActionProgressPoll | undefined,
  fallbackQueued: number
): BulkActionProgressSnapshot {
  const queued = bulkAction?.totalCount ?? fallbackQueued;
  const doneCount = bulkAction?.doneCount ?? 0;
  const errorCount = bulkAction?.errorCount ?? 0;
  const processed = doneCount + errorCount;
  const isComplete = bulkAction?.status === "DONE";
  const rawPercent = queued > 0 ? Math.round((processed / queued) * 100) : 0;
  const progressPercent = isComplete
    ? 100
    : Math.min(99, Math.max(0, rawPercent));

  return {
    queued,
    processed,
    doneCount,
    errorCount,
    isComplete,
    progressPercent,
  };
}

export function getBulkActionProgressToastCopy(
  actionType: BulkActionType,
  progress: BulkActionProgressSnapshot
): BulkActionProgressToastCopy {
  if (!progress.isComplete) {
    return {
      title: "Working hard",
      description: processingCopy(actionType, progress.queued),
    };
  }

  if (progress.errorCount === 0) {
    return {
      title: "Hooray!",
      description: successCopy(actionType, progress.doneCount),
    };
  }

  return {
    title: partialFailureCopy(actionType, progress.queued),
    description: `${progress.errorCount} ${getBulkActionNoun(
      actionType,
      progress.errorCount
    )} failed`,
  };
}
